import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '../../../services/translate.service';
import { GamificationService } from 'src/app/services/gamification.service';
import { take } from 'rxjs';
import { Company } from '@lighty';
import { StorageService } from '@services';
import { set } from 'date-fns';

interface Setting {
  label?: string;
  placeholder?: string;
  value: any;
  type: string;
  step?: number;
  min?: number;
  required?: boolean;
  selected?: number;
}

interface Level {
  title: string;
  settings: Setting[];
  number: number;
  can_delete: boolean;
}

interface PointsSection {
  title: string;
  description: string;
  [key: string]: any;
}

@Component({
  selector: 'app-gamification-settings',
  templateUrl: './gamification-settings.component.html',
  styleUrls: ['./gamification-settings.component.scss']
})
export class GamificationSettingsComponent implements OnInit {
  public loaded = false;
  public gamificationData: any = {};
  public basicData: any = {};
  public pointsData: any = {};
  public levelsData: any = {};
  public badgePacks: { id: number; title: string; path: string }[] = [];
  public selectedPackPath: string = '1';
  public company: Company;

  constructor(
    private cd: ChangeDetectorRef,
    private translate: TranslateService,
    private gamificationService: GamificationService,
    private storageService: StorageService,
  ) { }

  ngOnInit(): void {
    this.company = this.storageService.get('company');
    this.getGamificationData();
  }


  loadBasicData(): void {
    this.basicData = {
      title: "words-gamification.welcome",
      description: "words-gamification.description",
      enabled: this.gamificationData.enabled
    };
    this.selectedPackPath = this.gamificationData.badgesSection?.selectedPackPath || '1';
  }

  loadPointsData(): void {
    this.pointsData = {
      title: "words-gamification.point_section_title",
      description: "words-gamification.point_section_description",
      sections: this.getPointSections()
    };
  }

  loadLevelsData(): void {
    const section = this.gamificationData.gamificationLevelsSection;

    this.badgePacks = this.gamificationData.badgesSection.badges.map((b, idx) => ({
      id: idx + 1,
      title: b.title,
      path: b.path,
      number: b.number
    }));

    console.log("this.badgePacks", this.badgePacks);

    //this.selectedPackPath = this.badgePacks[0]?.path || '';

    this.levelsData = {
      title: section.title,
      levels: this.getLevelsData()
    };
  }

  getPointSections(): any[] {
    const sections = [];
    const sectionKeys = Object.keys(this.gamificationData.pointsSection)
      .filter(key => key.endsWith('_section') && key !== 'title' && key !== 'description');

    sectionKeys.forEach(key => {
      sections.push({
        name: key,
        ...this.gamificationData.pointsSection[key]
      });
    });

    return sections;
  }

  getLevelsData(): Level[] {
    return this.gamificationData.gamificationLevelsSection.levels.map((level: any) => ({
      ...level,
      textSettings: level.settings.filter((s: any) => s.type === 'text'),
      numberSettings: level.settings.filter((s: any) => s.type === 'number'),
      selectSettings: level.settings.filter((s: any) => s.type === 'select')
    }));
  }

  generateInsignias(path: string, count: number = 15): any[] {
    return Array.from({ length: count }, (_, i) => {
      const id = i + 1;
      return {
        id,
        name: 'words.badge' + '-' + id,
        img: this.getBadgeImagePath(id)
      };
    });
  }

  getActiveInsignias(): any[] {
    return this.generateInsignias(this.selectedPackPath);
  }

  onPackChange(): void {
    // Resetear selecciones de insignias
    this.levelsData.levels.forEach(level => {
      level.selectedInsignia = null;
      const selectSetting = level.settings.find((s: any) => s.type === 'select');
      if (selectSetting) selectSetting.selected = null;
    });

    // Opcional: Persistir el pack seleccionado en algún campo
    const badgeSection = this.gamificationData.badgesSection;
    this.gamificationData.badgesSection.selectedPackPath = this.selectedPackPath;
    badgeSection.selectedPath = this.selectedPackPath;
    console.log("Insignias activas:", this.selectedPackPath);
  }

  onBadgeSelected(levelIndex: number, newBadge: any) {
    const levels = this.levelsData.levels;
    const currentLevel = levels[levelIndex];
    const previousBadge = currentLevel.selectedInsignia;

    if (newBadge.id === previousBadge?.id) return;

    const otherIndex = levels.findIndex((l, i) =>
      i !== levelIndex && l.selectedInsignia?.id === newBadge.id
    );

    if (otherIndex !== -1) {
      levels[otherIndex].selectedInsignia = previousBadge;
      const otherSetting = levels[otherIndex].settings.find(s => s.type === 'select');
      otherSetting.selected = previousBadge?.id ?? null;
    }

    currentLevel.selectedInsignia = newBadge;
    const currentSetting = currentLevel.settings.find(s => s.type === 'select');
    if (currentSetting) currentSetting.selected = newBadge.id;
  }

  log(event: any) {
    console.log('Seleccionado:', event);
  }


  // Datos completos (simulados o desde servicio)

  trackByLevel(index: number, level: Level): number {
    return level.number;
  }

  trackBySection(index: number, section: any): string {
    return section.name;
  }

  public getGamificationData(): void {
    this.loaded = false;
    this.gamificationService.getGamificationData(this.company.id).pipe(take(1)).subscribe((data) => {
      if (data && data.data && data.data !== null) {
        this.gamificationData = data.data;
      } else {
        this.gamificationData = this.gamificationService.getFullData();
      }
      console.log("Gamification Data:", this.gamificationData);
      this.loadBasicData();
      this.loadPointsData();
      this.loadLevelsData();
      this.loaded = true;
    }
    );
  }

  public saveSettings(): void {
    this.gamificationData.pointsSection = this.pointsData;
    this.gamificationData.gamificationLevelsSection = this.levelsData;
    const params = this.gamificationData;
    this.gamificationService.updateGamificationData(this.company.id, params).pipe(take(1)).subscribe();
  }

  public getBadgeImagePath(index: number): string {
    return `assets/badges/pack-0${this.selectedPackPath}/${index}.png`
  }
}